<template>
  <div class="row child-row">
    <div class="col-4 pr-default pl-0">
      <div class="child-row__title pb-4">Details</div>

      <PackingInformation :shipment="shipment" />

      <ShipmentCarrier :shipment="shipment" />

      <ShipmentServicePoint :shipment="shipment" />

      <div class="pb-3">
        <div class="child-row-item__title">Weight</div>
        <div class="child-row-item__subtitle">{{ shipment.weight }} grams</div>
      </div>

      <div class="pb-3">
        <div class="child-row-item__title">Dimensions</div>
        <div class="child-row-item__subtitle">
          {{ shipment.height }} x {{ shipment.length }} x {{ shipment.width }} cm
        </div>
      </div>

      <AssignmentLink :shipment="shipment" />

      <div class="pb-3">
        <div class="child-row-item__title">Distribution channel</div>
        <div class="child-row-item__subtitle">
          {{ getDistributionChannelTxt(shipment.distributionChannel) }}
        </div>
      </div>

      <ShipmentLabel :shipment="shipment" :url="shipment.labelUrl" type="Label" />

      <ShipmentLabel
        v-if="shipment.documentsUrl"
        :url="shipment.documentsUrl"
        :shipment="shipment"
        type-name="Documents"
      />

      <div class="pb-3">
        <div class="child-row-item__title">Service level</div>
        <div class="child-row-item__subtitle">
          {{ ucFirst(shipment.serviceLevel) }}
        </div>
      </div>

      <div class="pb-3">
        <div class="child-row-item__title">Item reference</div>
        <div class="child-row-item__subtitle">
          {{ shipment.itemReference }}
        </div>
      </div>

      <ShipmentCost :shipment="shipment" />

      <div v-if="isStaff" class="pb-3">
        <div class="child-row-item__title">Customer</div>
        <div class="child-row-item__subtitle">
          <div>
            {{ addressCompany(shipment.company) }}
          </div>
          <div>
            <small>
              <b>All:</b>

              <router-link class="green-link ml-2" :to="allShipmentsRoute">shipments</router-link>

              <router-link class="green-link ml-2" :to="allAssignmentsRoute">assignments</router-link>
            </small>
          </div>
        </div>
      </div>

      <div v-if="isStaff" class="pb-3">
        <div class="child-row-item__title">Identifiers</div>
        <div class="child-row-item__subtitle">
          <div>
            Item:
            <small class="text-muted">{{ shipment.id }}</small>
          </div>
          <div>
            Assignment:
            <small class="text-muted">{{ shipment.assignmentId }}</small>
          </div>
          <div>
            Recipient address:
            <small class="text-muted">{{ shipment.recipient.id }}</small>
          </div>
          <div>
            Company:
            <small class="text-muted">{{ shipment.company.id }}</small>
          </div>
        </div>
      </div>
    </div>

    <div class="col-8 pr-default pl-0">
      <ContactData :shipment="shipment" />

      <ProxyDetails :shipment="shipment" />

      <div v-if="displayTracking" class="pb-4">
        <ShipmentTrackingEvents :shipment="shipment" />
      </div>

      <div v-if="displayMessages" class="pb-4">
        <ShipmentMessages :shipment="shipment" />
      </div>

      <CarrierMessagesTable v-if="isStaff && shipment.shouldUseProxy" :shipment="shipment" />

      <ShipmentOptimisationSummary v-if="isStaff" :shipment="shipment" />
    </div>
  </div>
</template>

<script lang="ts">
import type { Shipment } from '@/types/Models/Shipment'
import { computed, defineComponent, type PropType } from 'vue'
import { useAddressTransformers } from '@/composables/Address/useAddressTransformers'
import { LETTER_CARRIER } from '@/const/Carriers'
import { ucFirst } from '@/services/Helpers'
import { getDistributionChannelTxt } from '@/services/Utils'
import { useAccountStore } from '@/store/account'
import { STAFF_ASSIGNMENTS_LIST, STAFF_SHIPMENTS } from '@/types/router'
import ContactData from '@/views/Components/Shipments/Details/ContactData.vue'
import ProxyDetails from '@/views/Components/Shipments/Details/ProxyDetails.vue'
import ShipmentCost from '@/views/Components/Shipments/Details/ShipmentCost.vue'
import ShipmentLabel from '@/views/Components/Shipments/Details/ShipmentLabel.vue'
import AssignmentLink from '@/views/Components/Shipments/Details/AssignmentLink.vue'
import ShipmentCarrier from '@/views/Components/Shipments/Details/Carrier.vue'
import ShipmentMessages from '@/views/Components/Shipments/Messages.vue'
import PackingInformation from '@/views/Components/Shipments/Details/PackingInformation.vue'
import CarrierMessagesTable from '@/views/Components/Shipments/CarrierMessages.vue'
import ShipmentServicePoint from '@/views/Components/Shipments/Details/ServicePoint.vue'
import ShipmentTrackingEvents from '@/views/Components/Shipments/Events/TrackingEvents.vue'
import ShipmentOptimisationSummary from '@/views/Components/Shipments/Details/OptimisationSummary.vue'

export default defineComponent({
  name: 'ShipmentDetails',
  components: {
    PackingInformation,
    CarrierMessagesTable,
    ContactData,
    ProxyDetails,
    ShipmentCarrier,
    ShipmentServicePoint,
    ShipmentOptimisationSummary,
    ShipmentCost,
    AssignmentLink,
    ShipmentMessages,
    ShipmentTrackingEvents,
    ShipmentLabel,
  },
  props: {
    shipment: {
      type: Object as PropType<Shipment>,
      required: true,
    },
  },
  setup(props) {
    const accountStore = useAccountStore()
    const isStaff = computed(() => accountStore.isStaff)

    const allShipmentsRoute = { name: STAFF_SHIPMENTS, query: { customer: [props.shipment.company.id] } }
    const allAssignmentsRoute = { name: STAFF_ASSIGNMENTS_LIST, query: { customerId: [props.shipment.company.id] } }

    const { addressCompany } = useAddressTransformers()

    const displayTracking = computed(() => props.shipment.carrierHandle !== LETTER_CARRIER)
    const displayMessages = computed(() => props.shipment.carrierHandle !== LETTER_CARRIER)

    return {
      isStaff,
      ucFirst,
      addressCompany,
      getDistributionChannelTxt,
      allShipmentsRoute,
      allAssignmentsRoute,
      displayTracking,
      displayMessages,
    }
  },
})
</script>
